import { type Me, MeSchema } from '@gestion-permisos/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useState } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useApi } from '../api/api-context.js';
import { ApiProblem } from '../api/problem.js';
import { ApiErrorView } from '../components/api-error-view.js';

const ME_QUERY_KEY = ['me'] as const;
const MeContext = createContext<Me | undefined>(undefined);

/** Empleado de la sesión (`GET /api/v1/me`). Solo dentro de las rutas autenticadas. */
export function useMe(): Me {
  const me = useContext(MeContext);
  if (me === undefined) throw new Error('useMe() fuera de <SessionGate>.');
  return me;
}

/**
 * Arranque de las rutas autenticadas: `GET /api/v1/me`. Un 401 lleva, con una navegación de página
 * completa, al inicio de sesión con la ruta actual como `returnTo` (lo hace el cliente). Un 403
 * `NO_ACTIVE_EMPLOYEE` (sesión de un empleado desactivado) lleva a la pantalla de acceso denegado; los
 * demás errores se muestran con `ApiErrorView`.
 */
export function SessionGate() {
  const api = useApi();
  const me = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => api.get('/api/v1/me', MeSchema, { onUnauthorized: 'login' }),
  });

  if (me.isPending) return <Status text="Cargando la sesión…" />;
  if (me.isError) {
    const error = me.error;
    if (error instanceof ApiProblem && error.status === 401) return <Status text="Redirigiendo al inicio de sesión…" />;
    return (
      <main className="min-h-screen bg-background text-foreground">
        <div className="mx-auto max-w-xl p-6">
          <ApiErrorView error={error} />
        </div>
      </main>
    );
  }
  return (
    <MeContext.Provider value={me.data}>
      <Outlet />
    </MeContext.Provider>
  );
}

/** Cierre de sesión: `POST /api/v1/auth/logout` (con CSRF) y pantalla de sesión cerrada. */
export function useLogout(): { logout: () => void; pending: boolean; error: unknown } {
  const api = useApi();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const logout = () => {
    setPending(true);
    setError(undefined);
    api
      .mutate('POST', '/api/v1/auth/logout')
      .then(() => {
        api.forgetCsrf();
        queryClient.clear();
        void navigate('/sesion-cerrada', { replace: true });
      })
      .catch((failure: unknown) => {
        // Un 401 ya llevó a la pantalla de sesión expirada (lo hace el cliente).
        if (!(failure instanceof ApiProblem && failure.status === 401)) setError(failure);
        setPending(false);
      });
  };
  return { logout, pending, error };
}

function Status({ text }: { text: string }) {
  return (
    // Fuera del marco, como las pantallas públicas: el fondo y el color de la aplicación cubren toda la ventana.
    <main className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-xl p-6">
        <output className="block text-muted-foreground">{text}</output>
      </div>
    </main>
  );
}
